import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { auditApi, type AuditEntry } from '../lib/api';

const METHOD_COLOR: Record<string, 'info' | 'success' | 'warning' | 'error' | 'primary'> = {
  GET: 'info',
  POST: 'success',
  PUT: 'primary',
  PATCH: 'warning',
  DELETE: 'error',
};

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [viewing, setViewing] = useState<AuditEntry | null>(null);

  const load = (targetPage = page) => {
    setLoading(true);
    auditApi
      .list({
        page: targetPage,
        limit: 50,
        resource: resourceFilter || undefined,
        from: from || undefined,
        to: to || undefined,
      })
      .then((res) => {
        setEntries(res.data);
        setPageCount(res.pagination.pages);
        setTotal(res.pagination.total);
      })
      .catch(() => setError('Failed to load audit log'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setPage(1);
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceFilter, from, to]);

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>
        Audit Log
        <Typography component="span" variant="body2" color="text.secondary" ml={1}>
          ({total.toLocaleString()} total)
        </Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Every successful POST/PUT/PATCH/DELETE on an admin route is recorded here.
      </Typography>

      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          label="Resource"
          placeholder="e.g. apiKeys"
          value={resourceFilter}
          onChange={(e) => setResourceFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          label="From"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No audit entries</TableCell>
                </TableRow>
              ) : (
                entries.map((e) => (
                  <TableRow
                    key={e._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setViewing(e)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(e.at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={e.method ?? '?'}
                        color={e.method ? METHOD_COLOR[e.method] : 'default'}
                        sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 64 }}
                      />
                    </TableCell>
                    <TableCell>{e.resource}</TableCell>
                    <TableCell>
                      <code style={{ fontSize: 12 }}>{e.path ?? '—'}</code>
                    </TableCell>
                    <TableCell>{e.actor?.email ?? e.actor?.sub ?? '—'}</TableCell>
                    <TableCell align="right">{e.statusCode ?? '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      {viewing && (
        <Box
          onClick={() => setViewing(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            sx={{ maxWidth: 720, width: '100%', maxHeight: '80vh', overflow: 'auto', p: 3 }}
          >
            <Typography variant="h6" fontWeight={700} mb={1}>
              {viewing.method} {viewing.resource}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(viewing.at).toLocaleString()} · {viewing.actor?.email ?? viewing.actor?.sub ?? '—'} ·{' '}
              {viewing.statusCode}
            </Typography>
            <Box
              component="pre"
              sx={{
                mt: 2,
                p: 2,
                bgcolor: '#0f172a',
                color: '#e2e8f0',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 12.5,
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(viewing.diff ?? {}, null, 2)}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
